import { compareDecimal, multiplyDecimal, parseDecimal, sumDecimalQuotients } from './decimal.js';
import { shippingTranslations } from '../constants/shipping-summary.js';
import { isSupportedLocale } from '../constants/app.js';
export function packagingLabel(type, language) {
    return type.translations[language.split('-')[0]] ?? null;
}
/** No pack levels are added together. Each metric represents the same goods. */
export function calculateShipping(lines, settings) {
    const relevant = lines.filter((line) => line.itemType === 'ARTICLE' &&
        line.basis?.productType !== 'SERVICE' &&
        compareDecimal(line.quantityBase, '0') !== 0);
    const issues = [];
    const metric = (field, resolve) => {
        const terms = [];
        for (const line of relevant) {
            const term = line.basis === null || line.basis.productType === null
                ? 'missingBasis'
                : compareDecimal(line.quantityBase, '0') < 0
                    ? 'negativeQuantity'
                    : resolve(line.basis);
            if (typeof term === 'string') {
                issues.push({
                    position: line.position,
                    productId: line.productId,
                    name: line.name,
                    field,
                    reason: term,
                });
            }
            else {
                const weight = term.weight ?? '1';
                terms.push({
                    numerator: multiplyDecimal(line.quantityBase, weight, parseDecimal(line.quantityBase).scale + parseDecimal(weight).scale),
                    denominator: term.factor,
                });
            }
        }
        const complete = !issues.some((issue) => issue.field === field);
        return { value: relevant.length && complete ? sumDecimalQuotients(terms, 18) : null, complete };
    };
    const packages = settings.packagingEnabled
        ? settings.packagingTypeIds.map((typeId) => ({
            typeId,
            ...metric(typeId, (basis) => {
                const variants = basis.packagings.filter((pack) => pack.packagingTypeId === typeId);
                const selected = variants.filter((pack) => pack.isSummaryDefault);
                const pack = selected.length === 1 ? selected[0] : variants.length === 1 ? variants[0] : undefined;
                return pack
                    ? { factor: pack.factor }
                    : variants.length
                        ? 'ambiguousVariant'
                        : 'missingFactor';
            }),
        }))
        : [];
    const empty = { value: null, complete: true };
    const pieceCount = settings.packagingEnabled
        ? metric('pieceCount', (basis) => ({
            factor: '1',
            weight: basis.baseUnit === 'PCE' ? '1' : '0',
        }))
        : empty;
    const netKg = settings.weightEnabled && settings.weightMode !== 'GROSS'
        ? metric('netKg', (basis) => {
            const weight = basis.massKgPerBase ?? basis.weightNetKg;
            return weight === null ? 'missingNet' : { factor: '1', weight };
        })
        : empty;
    const grossKg = settings.weightEnabled && settings.weightMode !== 'NET'
        ? metric('grossKg', (basis) => {
            const selected = basis.packagings.filter((pack) => pack.isWeightDefault);
            if (selected.length > 1)
                return 'ambiguousVariant';
            const pack = selected[0];
            if (pack)
                return pack.weightGrossKg === null
                    ? 'missingGross'
                    : { factor: pack.factor, weight: pack.weightGrossKg };
            return basis.weightGrossKg === null
                ? 'missingGross'
                : { factor: '1', weight: basis.weightGrossKg };
        })
        : empty;
    return { packages, pieceCount, netKg, grossKg, issues, relevantCount: relevant.length };
}
/** Fingerprint excludes descriptions/prices; stored bases and physical quantities matter. */
export function shippingBasisKey(lines) {
    return JSON.stringify(lines
        .filter((line) => line.itemType === 'ARTICLE')
        .map((line) => ({
        position: line.position,
        productId: line.productId,
        quantityBase: line.quantityBase,
        basis: line.basis,
    })));
}
export function shippingDisplayRows(summary, type, language) {
    if (!summary ||
        !summary.settings.documentTypes.some((entry) => entry === type) ||
        summary.calculation.relevantCount === 0)
        return [];
    const actual = summary.actual?.needsReview === false ? summary.actual : null;
    const result = [];
    if (summary.settings.packagingEnabled)
        for (const typeId of summary.settings.packagingTypeIds) {
            const packType = summary.types.find((entry) => entry.id === typeId);
            const label = packType ? packagingLabel(packType, language) : null;
            const confirmed = actual?.packages.find((entry) => entry.typeId === typeId);
            const calculated = summary.calculation.packages.find((entry) => entry.typeId === typeId);
            const value = confirmed?.quantity ?? (calculated?.complete ? calculated.value : null);
            if (label && value !== null && value !== undefined && compareDecimal(value, '0') > 0)
                result.push({ key: typeId, label, value, actual: confirmed !== undefined });
        }
    const pieceCount = summary.calculation.pieceCount;
    const locale = language.split('-')[0] ?? '';
    if (summary.settings.packagingEnabled &&
        pieceCount?.complete &&
        pieceCount.value !== null &&
        compareDecimal(pieceCount.value, '0') > 0 &&
        isSupportedLocale(locale))
        result.push({
            key: 'pieceCount',
            label: shippingTranslations[locale].pieceCount,
            value: pieceCount.value,
            actual: false,
        });
    if (summary.settings.weightEnabled)
        for (const key of ['netKg', 'grossKg']) {
            if ((key === 'netKg' && summary.settings.weightMode === 'GROSS') ||
                (key === 'grossKg' && summary.settings.weightMode === 'NET'))
                continue;
            const confirmed = actual?.[key];
            const calculated = summary.calculation[key];
            const value = confirmed ?? (calculated.complete ? calculated.value : null);
            if (value !== null && compareDecimal(value, '0') > 0)
                result.push({
                    key,
                    label: key,
                    value,
                    actual: confirmed !== undefined && confirmed !== null,
                });
        }
    return result;
}
//# sourceMappingURL=shipping-summary.js.map